import React, { useState, useRef, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const EXPORT_OPTIONS = {
  '/property-comparison-hub': [
    { id: 'pdf-cma', label: 'Export CMA Report (PDF)', icon: 'FileText', description: 'Client-ready comparative analysis' },
    { id: 'pdf-summary', label: 'Property Summary (PDF)', icon: 'File', description: 'Single-page property overview' },
    { id: 'csv-comps', label: 'Comparables Data (CSV)', icon: 'Table', description: 'Raw comparable property data' },
    { id: 'xlsx-analysis', label: 'Full Analysis (Excel)', icon: 'Sheet', description: 'Complete dataset with formulas' },
  ],
  '/market-trends-analytics': [
    { id: 'pdf-market', label: 'Market Report (PDF)', icon: 'FileText', description: 'Comprehensive market analysis' },
    { id: 'csv-trends', label: 'Trend Data (CSV)', icon: 'Table', description: 'Historical pricing data export' },
    { id: 'png-charts', label: 'Charts (PNG)', icon: 'Image', description: 'High-resolution chart images' },
  ],
  '/investment-roi-dashboard': [
    { id: 'pdf-roi', label: 'ROI Report (PDF)', icon: 'FileText', description: 'Investment analysis for clients' },
    { id: 'xlsx-cashflow', label: 'Cash Flow Model (Excel)', icon: 'Sheet', description: 'Editable financial projections' },
    { id: 'csv-portfolio', label: 'Portfolio Data (CSV)', icon: 'Table', description: 'Investment portfolio export' },
  ],
};

export default function ExportControlPanel({ currentPath = '/property-comparison-hub', onExport, className = '' }) {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(null);
  const panelRef = useRef(null);

  const options = EXPORT_OPTIONS?.[currentPath] || EXPORT_OPTIONS?.['/property-comparison-hub'];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef?.current && !panelRef?.current?.contains(e?.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e?.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  const handleExport = async (option) => {
    if (exporting) return;
    setExporting(option?.id);
    try {
      await onExport?.(option);
      // Simulate export delay
      await new Promise(r => setTimeout(r, 1200));
    } finally {
      setExporting(null);
      setOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={panelRef}>
      <Button
        variant="outline"
        size="sm"
        iconName="Download"
        iconPosition="left"
        iconSize={14}
        onClick={() => setOpen(prev => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Export
      </Button>
      {open && (
        <div
          className="export-dropdown"
          role="menu"
          aria-label="Export options"
        >
          <div className="px-4 py-2 border-b border-border">
            <p className="text-xs font-caption font-medium text-muted-foreground uppercase tracking-wide">
              Export Options
            </p>
          </div>

          {options?.map((option) => (
            <button
              key={option?.id}
              role="menuitem"
              onClick={() => handleExport(option)}
              disabled={!!exporting}
              className="export-option w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted flex-shrink-0">
                {exporting === option?.id ? (
                  <Icon name="Loader2" size={14} color="var(--color-primary)" className="animate-spin" />
                ) : (
                  <Icon name={option?.icon} size={14} color="var(--color-primary)" />
                )}
              </div>
              <div className="text-left">
                <p className="font-medium text-sm text-foreground">{option?.label}</p>
                <p className="text-xs text-muted-foreground">{option?.description}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}